/**
 * DayCell — one cell in the weeks × 7 grid. Stacks PublicationChips,
 * supports click-empty to add, and acts as a drop target for chip drag.
 *
 * When more than `maxVisible` chips stack in a cell, the last visible
 * chip is replaced by a "+N" overflow badge that opens a popover.
 */

import React, { useCallback, useState } from 'react';
import { Box, Popover, Stack, Typography } from '@mui/material';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import type { ContentPublication } from '../../types';
import { PublicationChip, type ChipDetailLevel } from './PublicationChip';

const MAX_VISIBLE_HIGH = 4;
const MAX_VISIBLE_COMPACT = 3;

export interface DayCellDropPayload {
  publicationId: string;
  targetDate: Date;
  targetWeekIndex: number;
  targetWeekdayIndex: number;
}

/** H.2 — payload when a topic from TopicBacklogSidebar is dropped onto a slot. */
export interface DayCellTopicDropPayload {
  topicId: string;
  targetDate: Date;
  targetWeekIndex: number;
  targetWeekdayIndex: number;
}

export interface DayCellProps {
  date: Date;
  dateKey: string;
  weekIndex: number;
  weekdayIndex: number;
  isToday: boolean;
  publications: ContentPublication[];
  colorsByStrategy: Record<string, string>;
  topicTitlesByPub: Record<string, string | undefined>;
  detailLevel: ChipDetailLevel;
  onAddClick?: (date: Date, weekIndex: number, weekdayIndex: number) => void;
  onChipClick?: (pub: ContentPublication) => void;
  onChipDragStart?: (pub: ContentPublication) => void;
  onChipDragEnd?: () => void;
  onDrop?: (payload: DayCellDropPayload) => void;
  /** H.2 — fired when a Topic card from TopicBacklogSidebar is dropped here. */
  onTopicDrop?: (payload: DayCellTopicDropPayload) => void;
}

export const DayCell: React.FC<DayCellProps> = ({
  date,
  dateKey,
  weekIndex,
  weekdayIndex,
  isToday,
  publications,
  colorsByStrategy,
  topicTitlesByPub,
  detailLevel,
  onAddClick,
  onChipClick,
  onChipDragStart,
  onChipDragEnd,
  onDrop,
  onTopicDrop,
}) => {
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);
  const [isDragOver, setDragOver] = useState(false);

  const maxVisible = detailLevel === 'high' ? MAX_VISIBLE_HIGH : MAX_VISIBLE_COMPACT;
  const overflow = Math.max(0, publications.length - maxVisible);
  const visible = overflow > 0 ? publications.slice(0, maxVisible - 1) : publications;
  const hidden = overflow > 0 ? publications.slice(maxVisible - 1) : [];

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!isDragOver) setDragOver(true);
  }, [isDragOver]);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    // H.2 — topic-card drops set a custom MIME so we can distinguish them
    // from chip drag-rescheduling (text/plain = publicationId).
    const topicId = e.dataTransfer.getData('application/x-content-plan-topic-id');
    if (topicId) {
      onTopicDrop?.({ topicId, targetDate: date, targetWeekIndex: weekIndex, targetWeekdayIndex: weekdayIndex });
      return;
    }
    const pubId = e.dataTransfer.getData('text/plain');
    if (!pubId) return;
    onDrop?.({ publicationId: pubId, targetDate: date, targetWeekIndex: weekIndex, targetWeekdayIndex: weekdayIndex });
  }, [date, weekIndex, weekdayIndex, onDrop, onTopicDrop]);

  const isEmpty = publications.length === 0;

  return (
    <Box
      data-testid={isEmpty ? CONTENT_PLAN.CALENDAR_DAY_CELL_EMPTY : CONTENT_PLAN.CALENDAR_DAY_CELL}
      data-date={dateKey}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => isEmpty && onAddClick?.(date, weekIndex, weekdayIndex)}
      sx={{
        minHeight: detailLevel === 'high' ? 96 : 72,
        minWidth: 0,
        p: 0.75,
        borderRight: 1,
        borderBottom: 1,
        borderColor: 'divider',
        position: 'relative',
        bgcolor: isDragOver ? 'action.selected' : isToday ? 'action.hover' : 'transparent',
        cursor: isEmpty ? 'pointer' : 'default',
        transition: 'background-color 120ms ease',
        '&:hover': { bgcolor: isEmpty ? 'action.hover' : undefined },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontSize: 10,
          color: isToday ? 'primary.main' : 'text.secondary',
          fontWeight: isToday ? 700 : 500,
          mb: 0.5,
        }}
      >
        {date.getUTCDate()}
      </Typography>

      {visible.map((pub) => (
        <PublicationChip
          key={pub.id}
          publication={pub}
          color={colorsByStrategy[pub.strategyId] ?? '#666'}
          topicTitle={topicTitlesByPub[pub.id]}
          detailLevel={detailLevel}
          onClick={onChipClick}
          onDragStart={onChipDragStart}
          onDragEnd={onChipDragEnd}
        />
      ))}

      {overflow > 0 && (
        <Box
          data-testid={CONTENT_PLAN.CALENDAR_CHIP_OVERFLOW}
          onClick={(e) => {
            e.stopPropagation();
            setOverflowAnchor(e.currentTarget as HTMLElement);
          }}
          sx={{
            mt: 0.25,
            px: 0.75,
            py: 0.25,
            fontSize: 11,
            fontWeight: 600,
            color: 'text.secondary',
            bgcolor: 'action.hover',
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': { color: 'text.primary' },
          }}
        >
          +{overflow} more
        </Box>
      )}

      <Popover
        open={Boolean(overflowAnchor)}
        anchorEl={overflowAnchor}
        onClose={() => setOverflowAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack sx={{ p: 1, minWidth: 240, maxHeight: 320, overflow: 'auto' }} spacing={0.5}>
          {hidden.map((pub) => (
            <PublicationChip
              key={pub.id}
              publication={pub}
              color={colorsByStrategy[pub.strategyId] ?? '#666'}
              topicTitle={topicTitlesByPub[pub.id]}
              detailLevel="high"
              onClick={(p) => { setOverflowAnchor(null); onChipClick?.(p); }}
            />
          ))}
        </Stack>
      </Popover>
    </Box>
  );
};
